/**
 * Society event detail — full page with cover image upload, RSVP roster, and
 * reminders log. Linked from /society-events drawer "Full detail" button.
 */
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  Upload,
  CalendarHeart,
  Users,
  Bell,
  MapPin,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { api, get, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState } from '@/components/ui/empty-state';
import { TabsPills } from '@/components/ui/tabs-pills';
import { formatDate, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

type EventStatus = 'draft' | 'published' | 'cancelled' | 'completed';
type EventCategory =
  | 'festival'
  | 'cultural'
  | 'sports'
  | 'community_service'
  | 'educational'
  | 'meeting'
  | 'birthday_anniversary'
  | 'other';
type EventAudience = 'all' | 'owners_only' | 'tenants_only' | 'block' | 'unit';
type RsvpStatus = 'going' | 'maybe' | 'not_going';

interface SocietyEventDetail {
  id: string;
  title: string;
  description: string | null;
  category: EventCategory;
  audience: EventAudience;
  starts_at: string;
  ends_at: string | null;
  all_day: boolean;
  location: string | null;
  amenity_id: string | null;
  status: EventStatus;
  recurrence_rule: string | null;
  cover_image_storage_key: string | null;
  external_link: string | null;
  rsvp_enabled: boolean;
  rsvp_deadline: string | null;
  rsvp_max_attendees: number | null;
  reminder_day_offsets: string | null;
  last_reminder_sent_at: string | null;
  amenity?: { id: string; name: string } | null;
  rsvp_summary?: {
    going: number;
    maybe: number;
    not_going: number;
    guests_total: number;
  };
}

interface RsvpRow {
  id: string;
  responder_user_id: string;
  responder_unit_id: string | null;
  status: RsvpStatus;
  guests_count: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface AuditRow {
  id: string;
  action: string;
  actor_role: string;
  actor_user_id: string | null;
  after: Record<string, unknown> | null;
  at: string;
}

const STATUS_VARIANT: Record<EventStatus, 'muted' | 'info' | 'warn' | 'ok' | 'alert'> = {
  draft: 'muted',
  published: 'info',
  cancelled: 'alert',
  completed: 'ok'
};
const CATEGORY_LABEL: Record<EventCategory, string> = {
  festival: 'Festival',
  cultural: 'Cultural',
  sports: 'Sports',
  community_service: 'Community service',
  educational: 'Educational',
  meeting: 'Meeting',
  birthday_anniversary: 'Birthday / Anniversary',
  other: 'Other'
};

const RSVP_VARIANT: Record<RsvpStatus, 'muted' | 'info' | 'warn' | 'ok' | 'alert'> = {
  going: 'ok',
  maybe: 'warn',
  not_going: 'muted'
};

export function SocietyEventDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const [tab, setTab] = useState<'overview' | 'rsvps' | 'reminders'>('overview');
  const fileRef = useRef<HTMLInputElement>(null);
  const [coverTs, setCoverTs] = useState<number>(0);

  const detail = useQuery({
    queryKey: ['society-events', id],
    queryFn: () => get<SocietyEventDetail>(`/society-events/${id}`),
    enabled: !!id
  });

  const rsvps = useQuery({
    queryKey: ['society-events', id, 'rsvps'],
    queryFn: () =>
      get<{ items: RsvpRow[]; next_cursor: string | null; summary: SocietyEventDetail['rsvp_summary'] }>(
        `/society-events/${id}/rsvps`,
        { params: { limit: 100 } }
      ),
    enabled: !!id && tab === 'rsvps'
  });

  const reminders = useQuery({
    queryKey: ['society-events', id, 'reminders'],
    queryFn: () =>
      get<{ items: AuditRow[]; next_cursor: string | null }>('/audit-log', {
        params: {
          entity_type: 'society_event',
          entity_id: id,
          action: 'society_event.reminder_sent',
          limit: 50
        }
      }),
    enabled: !!id && tab === 'reminders'
  });

  const publish = useMutation({
    mutationFn: () => post(`/society-events/${id}/publish`, {}),
    onSuccess: () => {
      toast.success('Event published');
      void qc.invalidateQueries({ queryKey: ['society-events', id] });
      void qc.invalidateQueries({ queryKey: ['society-events'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const uploadCover = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/society-events/${id}/cover-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data as { cover_image_url: string; storage_key: string };
    },
    onSuccess: () => {
      toast.success('Cover image uploaded');
      setCoverTs(Date.now());
      void qc.invalidateQueries({ queryKey: ['society-events', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const e = detail.data;
  const coverUrl = useMemo(() => {
    if (!e?.cover_image_storage_key) return null;
    return `/v1/society-events/${id}/cover-image?ts=${coverTs || Date.now()}`;
  }, [e?.cover_image_storage_key, id, coverTs]);

  if (detail.isLoading) {
    return (
      <div className="flex flex-col gap-vt-4">
        <Skeleton className="h-12 w-1/2" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (detail.isError || !e) {
    return (
      <EmptyState
        icon={<CalendarHeart className="h-6 w-6" />}
        title="Event not found"
        description={detail.isError ? getErrorMessage(detail.error) : 'It may have been deleted.'}
        action={
          <Link to="/society-events">
            <Button variant="ghost">
              <ArrowLeft className="h-4 w-4" /> Back to Events
            </Button>
          </Link>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title={e.title}
        description={CATEGORY_LABEL[e.category]}
        actions={
          <div className="flex items-center gap-vt-2">
            <Link to="/society-events">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            </Link>
            {e.status === 'draft' ? (
              <Button onClick={() => publish.mutate()} disabled={publish.isPending}>
                Publish
              </Button>
            ) : null}
          </div>
        }
      />

      {/* Cover image */}
      <Card>
        <CardContent className="p-0">
          <div className="relative h-48 w-full overflow-hidden bg-vt-surface-2 sm:h-56">
            {coverUrl ? (
              <img
                src={coverUrl}
                alt="Event cover"
                className="h-full w-full object-cover"
                onError={() => undefined}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-vt-text-muted">
                <CalendarHeart className="h-12 w-12 opacity-30" />
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-vt-2 bg-gradient-to-t from-black/50 to-transparent px-vt-4 py-vt-3">
              <div className="flex items-center gap-vt-2">
                <Badge variant={STATUS_VARIANT[e.status]}>{e.status}</Badge>
                <span className="rounded-vt-pill bg-white/90 px-2 py-0.5 text-vt-11 font-medium text-vt-text">
                  {CATEGORY_LABEL[e.category]}
                </span>
              </div>
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(ev) => {
                    const f = ev.target.files?.[0];
                    if (f) uploadCover.mutate(f);
                    ev.target.value = '';
                  }}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="bg-white/85 text-vt-text hover:bg-white"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadCover.isPending}
                >
                  <Upload className="h-4 w-4" />
                  {uploadCover.isPending ? 'Uploading…' : coverUrl ? 'Replace cover' : 'Upload cover'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-vt-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardContent className="p-vt-4">
            <h3 className="text-vt-14 font-semibold text-vt-text">About</h3>
            <p className="mt-vt-2 whitespace-pre-line text-vt-14 text-vt-text">
              {e.description || 'No description provided.'}
            </p>
            {e.external_link ? (
              <div className="mt-vt-3">
                <span className="text-vt-12 text-vt-text-muted">External link: </span>
                <a
                  href={e.external_link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-vt-13 text-vt-primary underline"
                >
                  {e.external_link}
                </a>
              </div>
            ) : null}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4 space-y-vt-3">
            <h3 className="text-vt-14 font-semibold text-vt-text">When &amp; where</h3>
            <div className="flex items-start gap-vt-2 text-vt-13 text-vt-text">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-vt-text-muted" />
              <div>
                {e.all_day ? formatDate(e.starts_at) : formatDateTime(e.starts_at)}
                {e.ends_at ? (
                  <>
                    {' '}
                    – {e.all_day ? formatDate(e.ends_at) : formatDateTime(e.ends_at)}
                  </>
                ) : null}
                {e.recurrence_rule ? (
                  <div className="mt-1 text-vt-11 text-vt-text-muted">Repeats: {e.recurrence_rule}</div>
                ) : null}
              </div>
            </div>
            <div className="flex items-start gap-vt-2 text-vt-13 text-vt-text">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-vt-text-muted" />
              <div>{e.amenity?.name ?? e.location ?? 'No location specified'}</div>
            </div>
            <div className="flex items-start gap-vt-2 text-vt-13 text-vt-text">
              <Users className="mt-0.5 h-4 w-4 shrink-0 text-vt-text-muted" />
              <div className="capitalize">Audience: {e.audience.replace(/_/g, ' ')}</div>
            </div>
            {e.reminder_day_offsets ? (
              <div className="flex items-start gap-vt-2 text-vt-13 text-vt-text">
                <Bell className="mt-0.5 h-4 w-4 shrink-0 text-vt-text-muted" />
                <div>
                  Reminders: {e.reminder_day_offsets} day(s) from event
                  {e.last_reminder_sent_at ? (
                    <div className="mt-1 text-vt-11 text-vt-text-muted">
                      Last sent {formatDateTime(e.last_reminder_sent_at)}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* RSVP summary */}
      <Card>
        <CardContent className="p-vt-4">
          <h3 className="text-vt-14 font-semibold text-vt-text">RSVPs</h3>
          <div className="mt-vt-3 grid grid-cols-2 gap-vt-3 md:grid-cols-4">
            <RsvpStat label="Going" value={e.rsvp_summary?.going ?? 0} />
            <RsvpStat label="Maybe" value={e.rsvp_summary?.maybe ?? 0} />
            <RsvpStat label="Not going" value={e.rsvp_summary?.not_going ?? 0} />
            <RsvpStat label="Guests total" value={e.rsvp_summary?.guests_total ?? 0} />
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <CardContent className="p-vt-4">
          <TabsPills
            value={tab}
            onChange={(v) => setTab(v as 'overview' | 'rsvps' | 'reminders')}
            options={[
              { value: 'overview', label: 'Overview' },
              { value: 'rsvps', label: 'RSVP roster' },
              { value: 'reminders', label: 'Reminders log' }
            ]}
            aria-label="Detail tabs"
          />
          <div className="mt-vt-3">
            {tab === 'overview' ? (
              <div className="text-vt-13 text-vt-text-muted">
                See the cards above for at-a-glance details. Use “RSVP roster” to see
                individual responses, “Reminders log” to see when reminders fired.
              </div>
            ) : null}
            {tab === 'rsvps' ? (
              <RsvpRoster query={rsvps} />
            ) : null}
            {tab === 'reminders' ? (
              <RemindersLog query={reminders} />
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function RsvpStat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3 text-center">
      <div className="text-vt-22 font-semibold text-vt-text">{value}</div>
      <div className="mt-1 text-vt-11 uppercase tracking-wide text-vt-text-muted">{label}</div>
    </div>
  );
}

function RsvpRoster({
  query
}: {
  query: ReturnType<typeof useQuery<{ items: RsvpRow[]; next_cursor: string | null }>>;
}): JSX.Element {
  if (query.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="text-vt-13 text-vt-alert">
        Failed to load RSVPs: {getErrorMessage(query.error)}
      </div>
    );
  }
  const items = query.data?.items ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Users className="h-6 w-6" />}
        title="No RSVPs yet"
        description="Residents will appear here as they respond."
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Responder</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Guests</TableHead>
          <TableHead>Notes</TableHead>
          <TableHead>Responded at</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((r) => (
          <TableRow key={r.id}>
            <TableCell className="text-vt-12 text-vt-text-muted">
              {r.responder_user_id.slice(0, 8)}…
            </TableCell>
            <TableCell>
              <Badge variant={RSVP_VARIANT[r.status]}>{r.status.replace('_', ' ')}</Badge>
            </TableCell>
            <TableCell>{r.guests_count}</TableCell>
            <TableCell className="text-vt-13 text-vt-text-muted">{r.notes ?? '—'}</TableCell>
            <TableCell className="text-vt-13">{formatDateTime(r.updated_at)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RemindersLog({
  query
}: {
  query: ReturnType<typeof useQuery<{ items: AuditRow[]; next_cursor: string | null }>>;
}): JSX.Element {
  if (query.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="text-vt-13 text-vt-text-muted">
        Reminders log unavailable — audit-log endpoint may not accept these query params yet.
      </div>
    );
  }
  const items = query.data?.items ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Bell className="h-6 w-6" />}
        title="No reminders fired yet"
        description="The worker fires reminders based on the day-offsets configured on the event."
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>When</TableHead>
          <TableHead>Offset (days)</TableHead>
          <TableHead>Target day</TableHead>
          <TableHead>Actor</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((r) => (
          <TableRow key={r.id}>
            <TableCell className="text-vt-13">{formatDateTime(r.at)}</TableCell>
            <TableCell className="text-vt-13">
              {String(r.after?.offset_days ?? '—')}
            </TableCell>
            <TableCell className="text-vt-13">
              {String(r.after?.target_day ?? '—')}
            </TableCell>
            <TableCell className="text-vt-13 text-vt-text-muted">{r.actor_role}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

void cn;
